import { capitalCase } from 'change-case';
import castArray from 'lodash/castArray.js';
import filter from 'lodash/filter.js';
import find from 'lodash/find.js';
import get from 'lodash/get.js';
import includes from 'lodash/includes.js';
import last from 'lodash/last.js';
import sortBy from 'lodash/sortBy.js';

import { formatDuration, handleResponseError, initialState, relativeTimestamp } from '@/Helpers.js';
import MeetingLink from '@/models/MeetingLink.js';
import MeetingUser from '@/models/MeetingUser.js';
import Model from '@/models/Model.js';
import User from '@/models/User.js';
import axios from '@/plugins/axios.js';
import i18n from '@/plugins/i18n.js';
import moment from '@/plugins/vue-moment.js';

export default class Meeting extends Model {
    static entity = 'meetings';

    static modelName = 'Meeting';

    static fields() {
        return {
            id: this.attr(null),
            course_id: this.attr(null),
            host_id: this.attr(null),
            external_id: this.attr(null),
            provider: this.attr(null),
            identifier: this.attr(null),
            title: this.attr(null),
            description: this.attr(null),
            starts_at: this.attr(null),
            ends_at: this.attr(null),
            actual_started_at: this.attr(null),
            actual_ended_at: this.attr(null),
            participants_count: this.attr(null),
            start_url: this.attr(null),
            join_url: this.attr(null),
            took_place_at: this.attr(null),
            processed_at: this.attr(null),
            settings: this.attr(null),
            user_link: this.attr(null),

            ...super.fields(),

            host: this.belongsTo('users', 'host_id'),
            course: this.belongsTo('courses', 'course_id'),
            participants: this.hasMany('meeting_participants', 'meeting_id'),
            links: this.hasMany('meeting_links', 'meeting_id'),
            recordings: this.hasMany('meeting_recordings', 'meeting_id'),
            statuses: this.morphMany('statuses', 'model_id', 'model_type_entity'),
        };
    }

    static recent() {
        return Meeting.query()
            .where(meeting =>
                meeting.timestamp('starts_at').isBetween(moment().subtract(12, 'hours'), moment().add(12, 'hours'))
            )
            .where('processed_at', null);
    }

    static soon() {
        return Meeting.query().where(
            meeting =>
                meeting.timestamp('starts_at').isBetween(moment(), moment().add(12, 'hours')) ||
                moment().isBetween(meeting.timestamp('starts_at'), meeting.timestamp('ends_at'))
        );
    }

    static upcoming(include = ['course']) {
        return Meeting.query()
            .with(include)
            .where(meeting => meeting.timestamp('ends_at').isSameOrAfter(moment()));
    }

    static between(startDate, endDate) {
        return Meeting.query()
            .where(meeting => meeting.timestamp('ends_at').isSameOrAfter(moment(startDate)))
            .where(meeting => meeting.timestamp('starts_at').isSameOrBefore(moment(endDate)));
    }

    get viewable_statuses() {
        const viewable = initialState('MeetingSettings.display_statuses', []);

        return this.statuses.filter(status => viewable.includes(status.name));
    }

    get meeting_user() {
        const userId = this.$store().getters['Auth/userId'];

        return MeetingUser.query()
            .where('user_id', userId)
            .where('provider', this.provider)
            .orderBy('created_at', 'desc')
            .first();
    }

    get link() {
        const user = User.query().with(['roles', 'user_groups']).find(this.$store().getters['Auth/userId']);

        if (user.id === this.host_id || this.user_is_alternative_host) {
            return this.start_url;
        }

        if (get(this.user_link, 'link')) {
            return get(this.user_link, 'link');
        }

        if (user.isGroupInstructorOf(this.course_id) || user.is_admin) {
            return this.join_url;
        }

        return null;
    }

    get has_actual_times() {
        return !!this.actual_started_at && !!this.actual_ended_at;
    }

    get scheduled_time_range() {
        const start = this.timestamp('starts_at').format('MMMM Do h:mm a');
        const end = this.timestamp('ends_at').format('h:mm a zz');

        return `${start} - ${end}`;
    }

    get actual_time_range() {
        const start = this.timestamp('actual_started_at').format('MMMM Do h:mm a');
        const end = this.timestamp('actual_ended_at').format('h:mm a zz');

        return `${start} - ${end}`;
    }

    get scheduled_duration() {
        const difference = this.timestamp('ends_at').diff(this.timestamp('starts_at'));

        return formatDuration(moment.duration(difference));
    }

    get actual_duration() {
        if (!this.actual_started_at || !this.actual_ended_at) {
            return null;
        }

        const difference = this.timestamp('actual_ended_at').diff(this.timestamp('actual_started_at'));

        return formatDuration(moment.duration(difference));
    }

    get actual_duration_abbreviated() {
        if (!this.actual_started_at || !this.actual_ended_at) {
            return null;
        }

        const difference = this.timestamp('actual_ended_at').diff(this.timestamp('actual_started_at'));

        return formatDuration(moment.duration(difference), true);
    }

    get duration() {
        const start = this.has_actual_times ? this.timestamp('actual_started_at') : this.timestamp('starts_at');
        const end = this.has_actual_times ? this.timestamp('actual_ended_at') : this.timestamp('ends_at');

        const difference = end.diff(start);

        return formatDuration(moment.duration(difference));
    }

    get duration_abbreviated() {
        const start = this.has_actual_times ? this.timestamp('actual_started_at') : this.timestamp('starts_at');
        const end = this.has_actual_times ? this.timestamp('actual_ended_at') : this.timestamp('ends_at');

        const difference = end.diff(start);

        return formatDuration(moment.duration(difference), true);
    }

    get schedule_label() {
        return relativeTimestamp(this.timestamp('starts_at'));
    }

    get showable_settings() {
        if (this.provider === 'zoom') {
            return this.showable_zoom_settings;
        }

        return null;
    }

    get router_link() {
        return {
            name: 'course-meeting-view',
            params: { courseId: this.course_id, meetingId: this.id },
        };
    }

    get external_link() {
        if (this.provider === 'zoom') {
            return `https://stanbridge.zoom.us/meeting/${this.identifier}`;
        }

        return null;
    }

    get showable_zoom_settings() {
        return [
            { key: 'host_video' },
            { key: 'participant_video' },
            { key: 'join_before_host' },
            { key: 'mute_upon_entry' },
            { key: 'use_pmi', label: 'Use PMI' },
            {
                key: 'approval_type',
                description: {
                    0: 'Auto approve',
                    1: 'Manually approve',
                    2: 'No registration required',
                },
            },
            {
                key: 'audio',
                description: {
                    both: 'Both Telephone and VoIP',
                    telephony: 'Telephone only',
                    voip: 'VoIP only',
                },
            },
            {
                key: 'auto_recording',
                description: { local: 'Local', cloud: 'Cloud', none: 'None' },
            },
            { key: 'enforce_login' },
            {
                key: 'enforce_login_domains',
                label: 'Enforce login over domains',
                tooltip: 'Only signed in users with specified domains can join meetings.',
            },
            { key: 'waiting_room' },
            { key: 'meeting_authentication' },
        ];
    }

    get unjoinable_reason() {
        const user = User.query().with('meeting_users').find(this.$store().getters['Auth/userId']);

        if (user.meeting_users.length === 0) {
            return i18n.global.t('cannot_join_because_no_meeting_user_association', {
                provider: capitalCase(this.provider),
            });
        }

        const now = moment();

        if (now.isBefore(this.timestamp('starts_at').subtract(15, 'minutes'))) {
            return i18n.global.t('cannot_join_because_too_early');
        }

        if (now.isAfter(this.timestamp('ends_at').add(15, 'minutes'))) {
            return i18n.global.t('cannot_join_because_too_late');
        }

        return i18n.global.t('cannot_join_reason_unknown');
    }

    get user_is_alternative_host() {
        return this.meetingUserIsAlternativeHost(this.meeting_user);
    }

    get latest_status() {
        if (this.statuses.length === 0) {
            return null;
        }

        return last(sortBy(this.statuses, 'created_at'));
    }

    get took_place_outside_schedule() {
        if (!this.has_actual_times) {
            return false;
        }

        const difference = this.timestamp('actual_started_at').diff(this.timestamp('starts_at'), 'hours', true);

        return difference > 2.0 || difference < -2.0;
    }

    meetingUserIsAlternativeHost(meetingUser) {
        if (!meetingUser || !meetingUser.provider_email) {
            return false;
        }

        const alternativeHosts = get(this.settings, 'alternative_hosts', '').split(',');
        const providerEmail = get(meetingUser, 'provider_email');

        return includes(alternativeHosts, providerEmail);
    }

    isJoinable(timer) {
        if (!timer) return false;

        const user = User.query().with('meeting_users').find(this.$store().getters['Auth/userId']);

        if (get(user, 'meeting_users.length') === 0) {
            return false;
        }

        return timer.isBetween(
            this.timestamp('starts_at').subtract(15, 'minutes'),
            this.timestamp('ends_at').add(15, 'minutes')
        );
    }

    requestLink(enrollments) {
        const enrollment_ids = castArray(enrollments).map(enrollment => enrollment.id);

        try {
            return MeetingLink.api().post(`/api/meetings/${this.id}/links`, { enrollment_ids }, { errorHandle: false });
        } catch (error) {
            handleResponseError(error);
        }
    }

    pushToProvider() {
        return Meeting.api().post(`/api/meetings/${this.id}/push`, null, {
            errorHandle: false,
        });
    }

    async joinMeeting(timer) {
        if (!this.isJoinable(timer)) {
            return;
        }

        let { link } = this;

        if (!link) {
            const meetingLink = MeetingLink.query()
                .where('meeting_id', this.id)
                .where('meeting_user_id', get(this.meeting_user, 'id'))
                .first();

            link = get(meetingLink, 'link');
        }

        if (link) {
            window.open(link.toString(), '_blank');
        }
    }

    isHost(userId) {
        return this.host_id === userId;
    }

    canImportRecordings(user) {
        if (!user.is_admin || !this.processed_at) {
            return false;
        }

        const videoRecordings = filter(this.recordings, 'is_video');

        if (videoRecordings.length) {
            return videoRecordings.filter(videoRecording => videoRecording.recordable.is_stuck).length !== 0;
        }

        const threshold = initialState('MeetingSettings.recordings.minimum_time_before_force_import');

        const importStartedStatus = find(this.statuses, {
            name: 'import started',
        });

        if (
            importStartedStatus &&
            moment().isBefore(importStartedStatus.timestamp('created_at').add(threshold, 'minutes'))
        ) {
            return false;
        }

        return true;
    }

    importRecordings() {
        return axios.post(`/api/meetings/${this.id}/import-recordings`, [], {
            errorHandle: false,
        });
    }

    static searchable() {
        return ['title', 'description', 'identifier'];
    }
}
